import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import axAPI from "api/axios-instance";
import {
  CmsOperatorCreateValues,
  CmsOperatorInfo,
  CmsOperatorUpdateValues,
  CmsRoleFormValues,
  CmsRoleInfo,
} from "./types";

const ROLES_URL = "/cms/admin/roles";
const OPERATORS_URL = "/cms/admin/operators";
const ADMINS_URL = "/cms/admin/admins";

const errorText = (error: any, fallback: string) =>
  typeof error?.message === "string" ? error.message : fallback;

export const useAdminAccess = () => {
  const [roles, setRoles] = useState<CmsRoleInfo[]>([]);
  const [operators, setOperators] = useState<CmsOperatorInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesResponse, operatorsResponse] = await Promise.all([
        axAPI.get<CmsRoleInfo[]>(ROLES_URL),
        axAPI.get<CmsOperatorInfo[]>(OPERATORS_URL),
      ]);
      setRoles(rolesResponse.data || []);
      setOperators(operatorsResponse.data || []);
    } catch (error: any) {
      message.error(
        errorText(error, "Не удалось загрузить пользователей и роли")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const run = async (
    action: () => Promise<unknown>,
    successText: string,
    failText: string
  ) => {
    setSaving(true);
    try {
      await action();
      message.success(successText);
      await loadAll();
      return true;
    } catch (error: any) {
      message.error(errorText(error, failText));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    roles,
    operators,
    loading,
    saving,
    reload: loadAll,

    createRole: (values: CmsRoleFormValues) =>
      run(
        () => axAPI.post(ROLES_URL, values),
        "Роль создана",
        "Не удалось создать роль"
      ),
    updateRole: (id: number, values: CmsRoleFormValues) =>
      run(
        () => axAPI.put(`${ROLES_URL}/${id}`, values),
        "Роль сохранена",
        "Не удалось сохранить роль"
      ),
    deleteRole: (id: number) =>
      run(
        () => axAPI.delete(`${ROLES_URL}/${id}`),
        "Роль удалена",
        "Не удалось удалить роль"
      ),

    createOperator: (values: CmsOperatorCreateValues) =>
      run(
        () => axAPI.post(OPERATORS_URL, values),
        "Пользователь создан",
        "Не удалось создать пользователя"
      ),
    updateOperator: (keycloakId: string, values: CmsOperatorUpdateValues) =>
      run(
        () => axAPI.put(`${OPERATORS_URL}/${keycloakId}`, values),
        "Пользователь сохранён",
        "Не удалось сохранить пользователя"
      ),
    revokeOperator: (keycloakId: string) =>
      run(
        () => axAPI.delete(`${OPERATORS_URL}/${keycloakId}/access`),
        "Доступы сняты",
        "Не удалось снять доступы"
      ),
    deleteOperator: (keycloakId: string) =>
      run(
        () => axAPI.delete(`${OPERATORS_URL}/${keycloakId}`),
        "Пользователь удалён",
        "Не удалось удалить пользователя"
      ),

    resetTotp: (keycloakId: string) =>
      run(
        () => axAPI.delete(`${OPERATORS_URL}/${keycloakId}/totp`),
        "Двухфакторка сброшена — при следующем входе появится новый QR-код",
        "Не удалось сбросить двухфакторку"
      ),

    grantAdmin: (login: string) =>
      run(
        () => axAPI.post(ADMINS_URL, null, { params: { login } }),
        "Права администратора выданы",
        "Не удалось выдать права администратора"
      ),
    revokeAdmin: (login: string) =>
      run(
        () => axAPI.delete(ADMINS_URL, { params: { login } }),
        "Права администратора сняты",
        "Не удалось снять права администратора"
      ),
  };
};

export type AdminAccessApi = ReturnType<typeof useAdminAccess>;
